import { Component, Input } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { WixService } from 'src/app/Services/Wix-Service/wix.service';
import { NotificationsService } from 'src/app/Services/shared/notifications.service';
import { TranslocoService } from '@jsverse/transloco';
import { ShopifyService } from 'src/app/Services/shopify-service/shopify.service';
import { SquareSpaceService } from 'src/app/Services/square-space/square-space.service';
import { SquareupPosService } from 'src/app/Services/squareup-pos.service.ts/squareup-pos.service';
import { WoocomerceService } from 'src/app/Services/woocomerce-service/woocomerce.service';
import { NotificationTypes } from 'src/app/app.enums';
import { ArtistProduct } from 'src/app/models/artistproduct.model';
import { ViewArtistProductResponse } from 'src/app/models/viewartistproductresponse.model';

@Component({
    selector: 'app-single-product-sync',
    template: '',
    standalone: false
})
export class SingleProductSyncComponent {
  @Input() consignmentId!: string;
  @Input() selectedProductId!: string;
  @Input() AccountId!: string;
  @Input() selectedItem!: ViewArtistProductResponse;
  @Input() ArtistProductSummary!: ArtistProduct;
  @Input() SquareSpaceStoreId!: string;
  @Input() SquareSpaceStorePageId!: string;

  constructor(
    private wixService: WixService,
    private shopifyService: ShopifyService,
    private notificationsService: NotificationsService,
    private woocomerceService: WoocomerceService,
    private squareupPosService: SquareupPosService,
    private ngxSpinnerService: NgxSpinnerService,
    private squareSpaceService: SquareSpaceService,
    private transloco: TranslocoService
  ) { }

  // Utility: update or add a ProductSyncedOnPlatformDTO for a platform
  private updateProductSyncedOnPlatformDTO(
    platformName: string,
    newPlatformData: any,
    targetItem: { ProductSyncedOnPlatformDTOs?: any[] }
  ): void {
    if (!targetItem) return;
    const arr = targetItem.ProductSyncedOnPlatformDTOs || [];
    const idx = arr.findIndex(dto => dto?.PlatformName === platformName);
    if (idx > -1) {
      arr[idx] = { ...arr[idx], ...newPlatformData };
    } else {
      arr.push(newPlatformData);
    }
    targetItem.ProductSyncedOnPlatformDTOs = arr;
  }

  // Wix sync
  public syncProductWithWix(isManualSync: boolean): void {
    this.ngxSpinnerService.show();
    this.wixService.onProductSynctWix(this.selectedItem.ConsignmentId, this.selectedProductId!, isManualSync).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('Wix', response.ResponseData, this.selectedItem);
          this.selectedItem.WixProductId = response.ResponseData?.WixProductId;
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.productSync'),
            this.transloco.translate('singleProductSyncComponent.productSyncedSuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncProduct'),
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncProductError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // Square POS sync
  public onProductSynctoSquare(isManualSync: boolean): void {
    this.ngxSpinnerService.show();
    this.squareupPosService.onProductSyncsquareUp(
      this.selectedItem.ConsignmentId,
      this.selectedProductId!,
      isManualSync
    ).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();

        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('SquareUp', response.ResponseData, this.selectedItem);
          this.selectedItem.SquarePosProductId = response.ResponseData?.SquarePosProductId;

          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.productSync'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.productSyncedSuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncProduct'),
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncProductError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // WooCommerce sync (Gallery)
  public onProductSyncWoocommerce(isManualSync: boolean): void {
    this.ngxSpinnerService.show();
    this.woocomerceService.onProductSynctWoocommerce(
      this.selectedProductId!,
      this.selectedItem.ConsignmentId,
      isManualSync,
      this.AccountId
    ).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        const message = response.ResponseMessage || (response.Success ? this.transloco.translate('singleProductSyncComponent.productSyncedSuccessfully') : this.transloco.translate('singleProductSyncComponent.failedToSyncProduct'));
        const type = response.Success ? NotificationTypes.Success : NotificationTypes.Error;
        this.notificationsService.showNotification(
          response.Success ? this.transloco.translate('singleProductSyncComponent.syncSuccessful') : this.transloco.translate('singleProductSyncComponent.syncFailed'),
          message,
          type
        );
        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('WooCommerce', response.ResponseData, this.selectedItem);
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncProductError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // WooCommerce sync (Artist)
  public artistOnProductSyncWoocommerce(isManualSync: boolean): void {
    this.ngxSpinnerService.show();
    this.woocomerceService.artistOnProductSyncWoocommerce(
      this.selectedProductId!,
      this.AccountId,
      isManualSync,
    ).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        const message = response.ResponseMessage || (response.Success ? this.transloco.translate('singleProductSyncComponent.productSyncedSuccessfully') : this.transloco.translate('singleProductSyncComponent.failedToSyncProduct'));
        const type = response.Success ? NotificationTypes.Success : NotificationTypes.Error;
        this.notificationsService.showNotification(
          response.Success ? this.transloco.translate('singleProductSyncComponent.syncSuccessful') : this.transloco.translate('singleProductSyncComponent.syncFailed'),
          message,
          type
        );
        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('WooCommerce', response.ResponseData, this.ArtistProductSummary);
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncProductError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // Shopify sync (Artist)
  public artistOnProductSyncShopify(isManualSync: boolean): void {
    if (!this.AccountId || !this.selectedProductId) {
      this.notificationsService.showNotification(
        this.transloco.translate('singleProductSyncComponent.missingData'),
        this.transloco.translate('singleProductSyncComponent.shopifyIdMissing'),
        NotificationTypes.Error
      );
      return;
    }
    this.ngxSpinnerService.show();
    this.shopifyService.artistOnProductSyncShopify(
      this.selectedProductId,
      this.AccountId,
      isManualSync
    ).subscribe({
      next: (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('Shopify', response.ResponseData, this.ArtistProductSummary);
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncSuccessful'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.productSyncedShopifySuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncShopify'),
            NotificationTypes.Error
          );
        }
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncShopifyError'),
          NotificationTypes.Error
        );
      }
    });
  }

  // Shopify sync (Gallery)
  public onProductSyncToShopify(isManualSync: boolean): void {
    this.ngxSpinnerService.show();
    this.shopifyService.onProductSyncShopify(
      this.AccountId,
      this.selectedProductId!,
      this.selectedItem.ConsignmentId,
      isManualSync
    ).subscribe(
      (response) => {
        this.ngxSpinnerService.hide();
        if (response.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('Shopify', response.ResponseData, this.selectedItem);
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.productSync'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.productSyncedSuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncProduct'),
            NotificationTypes.Error
          );
        }
      },
      (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncProductError'),
          NotificationTypes.Error
        );
      }
    );
  }

  // SquareSpace sync (Gallery)
  public onProductSyncToSquareSpace(isManualSync: boolean): void {
    if (!this.selectedItem || !this.selectedProductId || !this.consignmentId) {
      this.notificationsService.showNotification(
        this.transloco.translate('singleProductSyncComponent.syncFailed'),
        this.transloco.translate('singleProductSyncComponent.requiredInfoMissing'),
        NotificationTypes.Error
      );
      return;
    }
    this.ngxSpinnerService.show();
    this.squareSpaceService.syncWithSquareSpace(
      this.SquareSpaceStoreId,
      this.SquareSpaceStorePageId,
      this.selectedProductId,
      this.consignmentId,
      isManualSync
    ).subscribe({
      next: (response) => {
        this.ngxSpinnerService.hide();
        if (response?.Success && response.ResponseData) {
          this.updateProductSyncedOnPlatformDTO('SquareSpace', response.ResponseData, this.selectedItem);
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncSuccessful'),
            response?.ResponseMessage || this.transloco.translate('singleProductSyncComponent.productSyncedSquareSpaceSuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response?.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncSquareSpace'),
            NotificationTypes.Error
          );
        }
      },
      error: (error) => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncSquareSpaceError'),
          NotificationTypes.Error
        );
      }
    });
  }

  // SquareSpace sync (Artist)
  public ArtistProductSyncToSquareSpace(isManualSync: boolean): void {
    if (!this.ArtistProductSummary || !this.selectedProductId) {
      this.notificationsService.showNotification(
        this.transloco.translate('singleProductSyncComponent.syncFailed'),
        this.transloco.translate('singleProductSyncComponent.requiredInfoMissing'),
        NotificationTypes.Error
      );
      return;
    }

    this.ngxSpinnerService.show();

    this.squareSpaceService.syncArtistProductWithSquareSpace(
      this.SquareSpaceStoreId,
      this.SquareSpaceStorePageId,
      this.selectedProductId,
      isManualSync
    ).subscribe({
      next: (response) => {
        this.ngxSpinnerService.hide();

        if (response?.Success) {
          this.updateProductSyncedOnPlatformDTO('SquareSpace', response.ResponseData, this.ArtistProductSummary);

          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncSuccessful'),
            response?.ResponseMessage || this.transloco.translate('singleProductSyncComponent.productSyncedSquareSpaceSuccess'),
            NotificationTypes.Success
          );
        } else {
          this.notificationsService.showNotification(
            this.transloco.translate('singleProductSyncComponent.syncFailed'),
            response?.ResponseMessage || this.transloco.translate('singleProductSyncComponent.failedToSyncSquareSpace'),
            NotificationTypes.Error
          );
        }
      },
      error: () => {
        this.ngxSpinnerService.hide();
        this.notificationsService.showNotification(
          this.transloco.translate('singleProductSyncComponent.syncFailed'),
          this.transloco.translate('singleProductSyncComponent.failedToSyncSquareSpaceError'),
          NotificationTypes.Error
        );
      }
    });
  }

}